import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../../redux/slices/authSlice';
import { postToChatAPI } from '../../../api/chatService';
import { CHATBOT_STRINGS } from '../../../constants';

export interface Message {
    sender: string;
    text: string;
    from: 'user' | 'bot';
}

export const useChat = () => {
    const user = useSelector(selectCurrentUser);
    const [messages, setMessages] = useState<Message[]>(() => {
        // Load messages from localStorage on initial render
        try {
            const savedMessages = localStorage.getItem('chatMessages');
            return savedMessages ? JSON.parse(savedMessages) : [];
        } catch (error) {
            console.error("Could not load messages from localStorage", error);
            return [];
        }
    });
    const [input, setInput] = useState('');
    const [loadingAnswer, setLoadingAnswer] = useState<boolean>(false);

    // Save messages to localStorage whenever they change
    useEffect(() => {
        try {
            localStorage.setItem('chatMessages', JSON.stringify(messages));
        } catch (error) {
            console.error("Could not save messages to localStorage", error);
        }
    }, [messages]);

    // Effect for the initial greeting message
    useEffect(() => {
        const getInitialMessage = async () => {
            setLoadingAnswer(true);
            try {
                const data = await postToChatAPI('Hola');
                const botMessage: Message = {
                    sender: CHATBOT_STRINGS.BOT_SENDER,
                    text: data.answer || data.response || CHATBOT_STRINGS.BOT_NO_RESPONSE,
                    from: 'bot',
                };
                setMessages([botMessage]);
            } catch (error) {
                console.error('Error fetching initial message:', error);
                const errorMessage: Message = {
                    sender: CHATBOT_STRINGS.BOT_SENDER,
                    text: CHATBOT_STRINGS.BOT_ERROR_MESSAGE,
                    from: 'bot',
                };
                setMessages([errorMessage]);
            }
            setLoadingAnswer(false);
        };

        // Only fetch initial message if there's no history
        if (messages.length === 0) {
            getInitialMessage();
        }
    }, []); // Empty dependency array ensures this runs only once on mount

    const handleSend = async (prompt: string) => {
        if (prompt.trim() === '') return;

        const userMessage: Message = {
            sender: user?.name || CHATBOT_STRINGS.USER_SENDER,
            text: prompt,
            from: 'user',
        };
        setMessages((prevMessages) => [...prevMessages, userMessage]);
        setInput('');
        setLoadingAnswer(true);

        try {
            const data = await postToChatAPI(prompt);
            const botMessage: Message = {
                sender: CHATBOT_STRINGS.BOT_SENDER,
                text: data.answer || data.response || CHATBOT_STRINGS.BOT_NO_RESPONSE,
                from: 'bot',
            };
            setMessages((prevMessages) => [...prevMessages, botMessage]);
        } catch (error) {
            console.error('Error fetching data:', error);
            const errorMessage: Message = {
                sender: CHATBOT_STRINGS.BOT_SENDER,
                text: CHATBOT_STRINGS.BOT_ERROR_MESSAGE,
                from: 'bot',
            };
            setMessages((prevMessages) => [...prevMessages, errorMessage]);
        }
        setLoadingAnswer(false);
    };

    return {
        messages,
        input,
        loadingAnswer,
        setInput,
        handleSend,
    };
};
